import React, { HTMLAttributes } from 'react';
import c from 'classnames';
import { ThemeOverride, type ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import type { ValidSurfaceRootComponent } from './types';

export type Props = {
  /**
   * "type" is about the relative importance of nearby sections. The higher the "type"
   * the more a region is called out to the user via borders, background, etc. {@link ThemeSurface.PRIMARY} is minimal
   * callout, {@link ThemeSurface.TERTIARY} is maximum callout.
   *
   * I think of this as us raising the element out of the page to catch the user's attention.
   */
  type: ThemeSurface;
  /**
   * When true, DS className is not addded to the root element.
   */
  themeOverrideOnly?: boolean;
  children: React.ReactNode;
  /**
   * What tag the root element should have. This should either be:
   * 1. An intrinsic element, e.g. `tag="section"`
   * 2. A simple component meant to replace an intrinsic element. e.g. `tag={Section}`, this `Section` component being
   *    from `@wix/da-a11y/pkg/components/headings/tagWrappers`
   *
   * It needs to be a block element. Inline won't work as intended.
   */
  tag?: ValidSurfaceRootComponent;
} & HTMLAttributes<HTMLOrSVGElement>;

const Surface = React.forwardRef<HTMLElement, Props>(
  ({ tag = 'div', type, themeOverrideOnly, className, ...rest }, ref) => {
    return (
      <ThemeOverride
        ref={ref}
        tag={tag}
        themeSurface={type}
        className={c(!themeOverrideOnly && `ds-${type}`, className)}
        {...rest}
      />
    );
  }
);
Surface.displayName = 'Surface';

export default Surface;
